<template>
    <div class="pt-6 px-6">
        <p class="text-sm text-white/80">
            We need to know your country to continue with the purchase.
        </p>
    </div>

    <div class="p-6">
        <div class="grid grid-cols-1 gap-4" v-if="hasFlag('stripe-payment')">
            <div>
                <label for="country" class="block text-white/80 text-sm font-medium mb-2">Country</label>
                <div class="relative">
                    <SelectInput
                        v-model="selectedCountry"
                        :options="countries" 
                        id="country"
                        placeholder="Select country" 
                        class="w-full"
                    ></SelectInput>
                </div>
            </div>

            <div>
                <label for="postal_code" class="block text-white/80 text-sm font-medium mb-2">Postal Code</label>
                <div class="relative">
                    <TextInput 
                        type="text" 
                        id="postal_code" 
                        placeholder="Postal Code" 
                        class="w-full" 
                        @input="validatePostalCode()" 
                        :disabled="!selectedCountry"
                        v-model="postalCode"
                    />
                </div>
                <p v-if="postalCodeError" class="text-red-500 text-xs mt-1">{{ postalCodeError }}</p>
            </div>

            <div class="mt-4">
                <button @click="handleSave" 
                    class="btn rounded-full bg-echoes-primary hover:bg-echoes-primary/80 border-echoes-primary hover:border-echoes-primary/80 text-white w-full">
                    <i class="mdi mdi-content-save"></i> Save and continue
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, defineEmits } from 'vue';
import axios from 'axios';
import { useLayoutComponents } from '@/composables/layout';
import { useStorage } from '@/composables/use-storage';
import { useToast } from '@/composables/use-toast';
import postalCodePatterns from '@/data/postalCodePatterns.json';

const { TextInput, SelectInput } = useLayoutComponents();
const { hasFlag, getUserInfo, saveUserInfo } = useStorage();
const { addToast } = useToast();

const emit = defineEmits(['save']);

const countries = ref([]);
const userInfo = ref(getUserInfo());
const selectedCountry = ref(userInfo.value?.country ?? '');
const postalCode = ref(userInfo.value?.postal_code ?? '');
const postalCodeError = ref('');

const fetchCountries = async () => {
    try {
        const response = await axios.get('https://restcountries.com/v3.1/all?fields=name,cca2');
        countries.value = response.data.map(country => ({
            label: country.name.common,
            value: country.cca2
        }));
    } catch (error) {
        console.error('Error fetching countries:', error);
        addToast('Failed to load countries list', 'error');
    }
};

const validatePostalCode = () => {
    postalCodeError.value = '';

    if (!postalCode.value) {
        postalCodeError.value = 'Postal code is required';
        return false;
    }

    if (!selectedCountry.value) {
        postalCodeError.value = 'Please select a country first';
        return false;
    }

    const pattern = postalCodePatterns[selectedCountry.value];
    if (pattern) {
        const regex = new RegExp('^' + pattern + '$');
        if (!regex.test(postalCode.value)) {
            postalCodeError.value = 'Invalid postal code format for selected country';
            return false;
        }
    }

    return true;
};

const handleSave = async () => {
    if (!selectedCountry.value) {
        addToast('Please select a country', 'error');
        return;
    }

    if (!validatePostalCode()) {
        return;
    }

    try {
        const response = await axios.post('/api/users/set-stripe-customer/' + userInfo.value.id, {
            country: selectedCountry.value,
            postal_code: postalCode.value
        });

        if (response.status === 201) {
            userInfo.value.country = selectedCountry.value;
            userInfo.value.postal_code = postalCode.value;
            saveUserInfo(userInfo.value);
            
            emit('save', {
                country: selectedCountry.value,
                postalCode: postalCode.value
            });
            
            emit('close');
        } else {
            addToast('Failed to update country', 'error');
        }
    } catch (error) {
        console.error('Error saving country:', error);
        addToast(error.response?.data?.message || 'Failed to update country', 'error');
    }
};

onMounted(() => {
    fetchCountries();
});
</script>